/**
 * Filters sibling orders by the primary coverage plan effective date range.
 * @module siblingOrderFilter/filterSiblingOrdersByPrimaryCoveragePlanDates
 */
import { SingleCoverage } from "../../../data-model/medicalPaOrdersModel";
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { logger, logPassed, logRejected } from "./logging";
import { isOrderDosWithinRange } from "./orderPredicates";

/** Minimal coverage payload shape consumed by the plan-date filter. */
export interface ParentOrderCoverageDetails {
  coverages?: SingleCoverage[];
}

/**
 * Resolves the primary coverage tier from the parent order's coverage details.
 *
 * @param parentOrderCoverageDetails - Parent order coverage payload
 * @returns The primary coverage entry, or undefined when not present
 */
const getPrimaryCoverage = (
  parentOrderCoverageDetails: ParentOrderCoverageDetails | null | undefined,
): SingleCoverage | undefined => {
  return parentOrderCoverageDetails?.coverages?.find(
    (coverage) => coverage.coverage_type === "primary",
  );
};

/** Options for {@link filterSiblingOrdersByCoveragePlanDateRange}. */
export interface FilterSiblingOrdersByCoveragePlanDateRangeOptions {
  /** Log prefix identifying the caller (defaults to `[Step 2]`). */
  logStep?: string;
}

/**
 * Filters sibling orders to those whose date of service falls within an
 * inclusive coverage plan effective date range.
 *
 * When either plan date is missing, or when no sibling orders fall within the
 * range, an empty array is returned.
 *
 * @param planStartDate - Inclusive plan start date (`service_year_from`)
 * @param planEndDate - Inclusive plan end date (`service_year_to`)
 * @param siblingOrders - Sibling orders to filter
 * @param options - Optional logging context
 * @returns Sibling orders whose date of service lies within the plan range,
 *   or an empty array when dates are missing or no orders match
 */
export const filterSiblingOrdersByCoveragePlanDateRange = (
  planStartDate: string | undefined,
  planEndDate: string | undefined,
  siblingOrders: SiblingOrder[],
  options: FilterSiblingOrdersByCoveragePlanDateRangeOptions = {},
): SiblingOrder[] => {
  const logStep = options.logStep ?? "[Step 2]";

  if (!planStartDate || !planEndDate) {
    logger.warn(
      `${logStep} coverage plan dates missing — service_year_from=${
        planStartDate ?? "n/a"
      } service_year_to=${planEndDate ?? "n/a"} — returning empty`,
    );
    return [];
  }

  logger.debug(
    `${logStep} coverage plan range: service_year_from=${planStartDate} service_year_to=${planEndDate}`,
  );

  const coveragePlanMatchedSiblingOrders = siblingOrders.filter((order) =>
    isOrderDosWithinRange(order, planStartDate, planEndDate),
  );

  logRejected(
    logStep,
    "date of service outside coverage plan range",
    siblingOrders,
    coveragePlanMatchedSiblingOrders,
  );
  logPassed(
    logStep,
    "date of service within coverage plan range",
    coveragePlanMatchedSiblingOrders,
  );

  if (coveragePlanMatchedSiblingOrders.length === 0) {
    logger.debug(
      `${logStep} no sibling orders within plan range ${planStartDate} to ${planEndDate} — returning empty`,
    );
    return [];
  }

  logger.debug(
    `${logStep} coverage plan date filter: ${coveragePlanMatchedSiblingOrders.length}/${siblingOrders.length} passed`,
  );

  return coveragePlanMatchedSiblingOrders;
};

/**
 * Returns sibling order ids whose date of service falls within the supplied
 * coverage plan date range.
 *
 * @param planStartDate - Inclusive plan start date (`service_year_from`)
 * @param planEndDate - Inclusive plan end date (`service_year_to`)
 * @param siblingOrders - Sibling orders to filter
 * @param options - Optional logging context
 * @returns Distinct sibling order ids within the plan range, or an empty array
 *   when dates are missing or no orders match
 */
export const getSiblingOrderIdsByCoveragePlanDateRange = (
  planStartDate: string | undefined,
  planEndDate: string | undefined,
  siblingOrders: SiblingOrder[],
  options: FilterSiblingOrdersByCoveragePlanDateRangeOptions = {},
): string[] => {
  return filterSiblingOrdersByCoveragePlanDateRange(
    planStartDate,
    planEndDate,
    siblingOrders,
    options,
  ).map((order) => order.order_id);
};

/**
 * Filters sibling orders to those whose date of service falls within the
 * primary coverage plan's inclusive effective date range.
 *
 * When the primary coverage plan start or end date is missing, or when no
 * sibling orders fall within the range, an empty array is returned.
 *
 * @param parentOrderCoverageDetails - Parent order coverage details containing
 *   the primary coverage plan dates
 * @param siblingOrders - Sibling orders to filter
 * @returns Sibling orders whose date of service lies within the primary plan
 *   range, or an empty array when dates are missing or no orders match
 */
export const filterSiblingOrdersByPrimaryCoveragePlanDates = (
  parentOrderCoverageDetails: ParentOrderCoverageDetails | null | undefined,
  siblingOrders: SiblingOrder[],
): SiblingOrder[] => {
  const primaryCoverage = getPrimaryCoverage(parentOrderCoverageDetails);
  return filterSiblingOrdersByCoveragePlanDateRange(
    primaryCoverage?.service_year_from,
    primaryCoverage?.service_year_to,
    siblingOrders,
  );
};
